import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../app/http-common';
import { useConfig } from './FrontendConfigContext';
import { useAuthState, useCanCreateBrief } from '../hooks/useFrontegg';

/**
 * OnboardingContext — first-run guidance layer state.
 *
 * Gating (all must hold for the guidance UI to render):
 *   - ONBOARDING_ENABLED config is on
 *   - user falls inside ONBOARDING_ROLLOUT_PERCENT (stable per-email bucket)
 *   - user can create briefs (readonly users never see the draft checklist)
 *   - user has not completed onboarding (completedAt unset), except for a
 *     short grace window right after completion so the panel can show the
 *     "all done" state before disappearing for good
 *
 * Server state lives in the UserOnboardingState collection so progress
 * follows the user across devices. `resetOnboarding` (user menu) clears it
 * and restarts the flow from the welcome modal.
 */

const OnboardingContext = createContext(null);

const COMPLETION_LINGER_MS = 8000;

const noop = () => {};
const DISABLED = {
    enabled: false,
    state: null,
    showWelcome: false,
    panelVisible: false,
    isDone: false,
    videoUrl: '',
    canReset: false,
    markStep: noop,
    finishOnboarding: noop,
    chooseWelcome: noop,
    setChecklistDismissed: noop,
    resetOnboarding: noop,
};

// Stable 0-99 bucket per user so rollout percentage doesn't flicker between sessions.
function rolloutBucket(seed) {
    let hash = 0;
    const str = String(seed || '');
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % 100;
}

export const useOnboarding = () => useContext(OnboardingContext) || DISABLED;

export const OnboardingProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuthState();
    const canCreateBrief = useCanCreateBrief();
    const config = useConfig();
    const [state, setState] = useState(null);
    const [justCompleted, setJustCompleted] = useState(false);
    const pendingSteps = useRef(new Set());
    const lingerTimerRef = useRef(null);

    const configEnabled = String(config.ONBOARDING_ENABLED ?? 'true') === 'true';
    const rolloutPercent = Number(config.ONBOARDING_ROLLOUT_PERCENT ?? 100);
    const videoUrl = String(config.ONBOARDING_VIDEO_URL || '');

    const inRollout = user?.email ? rolloutBucket(user.email) < rolloutPercent : false;
    const eligible = Boolean(isAuthenticated && canCreateBrief && configEnabled && inRollout && state);
    const enabled = eligible && (!state.completedAt || justCompleted);

    useEffect(() => {
        if (!isAuthenticated || !canCreateBrief || !configEnabled || !inRollout) return undefined;
        let cancelled = false;
        api.getOnboardingState()
            .then((data) => { if (!cancelled && data) setState(data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [isAuthenticated, canCreateBrief, configEnabled, inRollout]);

    useEffect(() => () => {
        if (lingerTimerRef.current) clearTimeout(lingerTimerRef.current);
    }, []);

    const patchState = useCallback((patch) => {
        api.updateOnboardingState(patch)
            .then((data) => { if (data) setState(data); })
            .catch(() => {});
    }, []);

    const startCompletionLinger = useCallback(() => {
        setJustCompleted(true);
        if (lingerTimerRef.current) clearTimeout(lingerTimerRef.current);
        lingerTimerRef.current = setTimeout(() => setJustCompleted(false), COMPLETION_LINGER_MS);
    }, []);

    const markStep = useCallback((step) => {
        setState((prev) => {
            if (!prev || prev.steps?.[step] || pendingSteps.current.has(step)) return prev;
            pendingSteps.current.add(step);
            api.updateOnboardingState({ completeStep: step })
                .then((data) => { if (data) setState(data); })
                .catch(() => {})
                .finally(() => pendingSteps.current.delete(step));
            return {
                ...prev,
                steps: { ...prev.steps, [step]: new Date().toISOString() },
            };
        });
    }, []);

    // Explicit finish: the user pressed Finish in the guide panel. Shows the
    // confetti celebration for a few seconds, then the layer retires for good.
    const finishOnboarding = useCallback(() => {
        setState((prev) => {
            if (!prev || prev.completedAt) return prev;
            startCompletionLinger();
            patchState({ complete: true });
            return { ...prev, completedAt: new Date().toISOString() };
        });
    }, [patchState, startCompletionLinger]);

    const chooseWelcome = useCallback((choice) => {
        // "Explore on my own" opts out entirely: mark the user as onboarded so
        // the guide never reappears (Restart onboarding in the user menu is
        // the way back in).
        const optsOut = choice === 'explore';
        setState((prev) => (prev ? {
            ...prev,
            welcomeChoice: choice,
            welcomeSeenAt: new Date().toISOString(),
            ...(optsOut ? { completedAt: prev.completedAt || new Date().toISOString() } : {}),
        } : prev));
        patchState(optsOut ? { welcomeChoice: choice, complete: true } : { welcomeChoice: choice });
    }, [patchState]);

    const setChecklistDismissed = useCallback((dismissed) => {
        setState((prev) => (prev ? { ...prev, checklistDismissed: dismissed } : prev));
        patchState({ checklistDismissed: dismissed });
    }, [patchState]);

    const resetOnboarding = useCallback(() => {
        setJustCompleted(false);
        patchState({ reset: true });
    }, [patchState]);

    const value = useMemo(() => {
        if (!eligible) return DISABLED;
        const isDone = Boolean(state.completedAt);
        return {
            enabled,
            state,
            showWelcome: enabled && !state.welcomeSeenAt,
            panelVisible: enabled && Boolean(state.welcomeSeenAt) && !state.checklistDismissed,
            isDone,
            videoUrl,
            // Re-activation is offered even after onboarding completed/dismissed.
            canReset: true,
            markStep,
            finishOnboarding,
            chooseWelcome,
            setChecklistDismissed,
            resetOnboarding,
        };
    }, [eligible, enabled, state, videoUrl, markStep, finishOnboarding, chooseWelcome, setChecklistDismissed, resetOnboarding]);

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
